import {useRef, useEffect, forwardRef, useState} from 'react';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import MinimizeIcon from '@mui/icons-material/Minimize';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import Slide from '@mui/material/Slide';
import Draggable from 'react-draggable';
import { isIOSMobile } from '../util/videoUtils';
import DefaultButton from './DefaultButton';


const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function MinimizableDialog({open, title, children, handleClose, showClose, fullScreen, width, maxWidth, height, maxHeight, zIndex}) {
  const [minimized, setMinimized] = useState(false);
  const [renderError, setRenderError] = useState(false);
  const useTouch= isIOSMobile();
  const ref = useRef();
  const useWidth= fullScreen ? '100%' : width ? width : "300px";
  const useHeight= fullScreen ? '100%' : height ? height : undefined;
  const useMaxHeight= fullScreen ? '100%' : maxHeight ? maxHeight : undefined;
  const useMaxWidth= fullScreen ? '100%' : maxWidth ? maxWidth : undefined;
  
  // Check if browser supports native dialog element - do this BEFORE any rendering
  const supportsDialog = typeof HTMLDialogElement === 'function';

  useEffect(() => {
    if (!supportsDialog) {
      console.error('Browser does not support native <dialog> element. Please upgrade your browser.');
      return;
    }
    
    try {
      if (open){
        if (!minimized) {
          ref.current?.showModal();
        } else {
          ref.current?.close();
        }
      }
    } catch (error) {
      console.error('Error opening dialog:', error);
      setRenderError(true);
    }
  }, [minimized, open, supportsDialog]);
  

  function minimize(){
	  setMinimized(true);
  }
  
  function restore(){
    console.log("RESTORE");
	  setMinimized(false);
  }
  
  const onClose = (event, reason) => {
    if (reason && reason === "backdropClick") 
      minimize();
    else
      handleClose();
  }
  
  
  function DragDialog({children}) {
    return (
      fullScreen ? children :
      <Draggable
        handle="#draggable-dialog-title"
        cancel={'[class*="dialog-content-root"]'}
      >
        {children}
      </Draggable>
    );
  }

  // Don't render anything if not open
  if (!open) return null;
  
  // Show fallback UI if browser doesn't support dialog or if there was a render error
  if (!supportsDialog || renderError) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <Paper style={{
          padding: '32px',
          borderRadius: '8px',
          maxWidth: '500px',
          margin: '16px'
        }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Browser Not Supported
          </Typography>
          <Typography variant="body1" paragraph sx={{ mt: 2 }}>
            This feature requires a modern browser with dialog element support. Please upgrade to:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>Safari 15.4 or later (macOS Monterey 12.3+)</li>
            <li>Chrome 37 or later</li>
            <li>Firefox 98 or later</li>
            <li>Edge 79 or later</li>
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Your current browser: {navigator.userAgent.split(' ').slice(-2).join(' ')}
          </Typography>
          <DefaultButton 
            onClick={() => {
              if (handleClose) handleClose();
            }} 
            variant="contained"
            color="primary"
            fullWidth
          >
            Close
          </DefaultButton>
        </Paper>
      </div>
    );
  }

  return (
    <>
      <dialog ref={ref}
              onCancel={minimize}
              style={{width: useWidth,
                      height: useHeight,
                      maxHeight: useMaxHeight,
                      maxWidth: useMaxWidth,
                      borderRadius: '5px',
                      overflow: 'auto'}}>
      <div className="flex flex-col w-full h-full">
        <AppBar id="draggable-dialog-title" sx={{ position: 'relative',
                                               zIndex: 1,
                                            '& .MuiToolbar-root': {
                                              minHeight: '48px',
                                              paddingLeft: '12px',
                                              paddingRight: '12px',
                                              height: '48px',
                                              width: '100%',
					                                    }}}>
          <Toolbar>
            <div className="flex flex-row w-full">
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                {title}
              </Typography>
              <IconButton
                edge="start"
                color="inherit"
                onClick={minimize}
                aria-label="minimize"
              >
                <MinimizeIcon />
              </IconButton>
              {showClose &&
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleClose}
                aria-label="close"
              >
                <FuseSvgIcon>heroicons-outline:x</FuseSvgIcon>
              </IconButton>
              }
            </div>
          </Toolbar>
        </AppBar>
        <div className="flex grow min-h-0 w-full dialog-content-root">
          {children}
        </div>
      </div>
      </dialog>
        
       <Draggable>
       <AppBar position="fixed" sx={{ display: !minimized ? 'none' : undefined, 
                                      width: '300px', 
                                      zIndex: 1300,
                                      top: 'auto',
                                      bottom: 0, 
                                      left: 0,
                                      '& .MuiToolbar-root': {
										paddingLeft: '10px',
                                        paddingRight: '10px',
                                        minHeight: '48px',
									  }}}>
          <Toolbar>
            <Typography noWrap sx={{ marginLeft: 2, flex: 1 }} variant="h6" component="div">
              {title}
            </Typography>
            <IconButton
              edge="start"
              color="inherit"
              onClick={!useTouch ? restore : undefined}
              onTouchStart={useTouch ? restore : undefined}
              aria-label="restore"
            >
              <FuseSvgIcon>heroicons-outline:window</FuseSvgIcon>
            </IconButton>
            {showClose &&
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <FuseSvgIcon>heroicons-outline:x</FuseSvgIcon>
            </IconButton>
            }
          </Toolbar>
        </AppBar>
        </Draggable>
    </>
  );
}
